import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ButtonLoadingContent, loadingButtonClass } from "@/components/buttons/ButtonLoadingContent";
import { Button } from "@/components/ui/button";
import { PREDEFINED_REASONS, TEXT } from "@/utils/constants/appConstants/commonConstants";
import { Textarea } from "@/components/ui/textarea";

interface SuspendUserModalProps {
    show?: boolean;
    onHide?: () => void;
    onSuspend: (reason: string) => void;
    loading?: boolean;
}

const SUSPENSION_REASONS = [...PREDEFINED_REASONS, "Other"];

const SuspendUserModal: React.FC<SuspendUserModalProps> = (props) => {
    const [selectedReason, setSelectedReason] = useState("");
    const [otherReason, setOtherReason] = useState("");
    const showOtherReason = selectedReason === "Other";

    const resetForm = () => {
        setSelectedReason("");
        setOtherReason("");
    };

    const handleClose = () => {
        resetForm();
        props.onHide?.();
    };

    const handleReasonChange = (value: string) => {
        setSelectedReason(value);
        if (value !== "Other") {
            setOtherReason("");
        }
    };

    const handleSubmit = () => {
        const finalReason = showOtherReason ? otherReason : selectedReason;
        if (finalReason.trim()) {
            props.onSuspend(finalReason.trim());
        }
    };

    const canSubmit =
        Boolean(selectedReason) &&
        (!showOtherReason || Boolean(otherReason.trim())) &&
        !props.loading;

    return (
        <Dialog
            open={props.show}
            onOpenChange={(open) => {
                if (!open) {
                    handleClose();
                }
            }}
        >
            <DialogContent className="gs-suspend-dialog sm:max-w-lg">
                <div className="gs-suspend-dialog__header">
                    <h4 className="gs-suspend-dialog__title" id="suspend-user-title">
                        {TEXT.SUSPEND_USER_TITLE}
                    </h4>
                </div>

                <div className="gs-suspend-dialog__body">
                    <p className="gs-suspend-dialog__label">{TEXT.SELECT_REASON}</p>
                    <div className="gs-suspend-dialog__reasons" role="radiogroup" aria-label={TEXT.SELECT_REASON}>
                        {SUSPENSION_REASONS.map((reason, index) => (
                            <label
                                key={reason}
                                htmlFor={`reason-${index}`}
                                className="gs-suspend-dialog__option"
                            >
                                <input
                                    type="radio"
                                    name="suspensionReason"
                                    id={`reason-${index}`}
                                    value={reason}
                                    checked={selectedReason === reason}
                                    onChange={() => handleReasonChange(reason)}
                                    className="gs-suspend-dialog__radio"
                                />
                                <span>{reason}</span>
                            </label>
                        ))}
                    </div>

                    <div className="gs-suspend-dialog__other">
                        <p className="gs-suspend-dialog__sublabel">Please specify the reason</p>
                        <Textarea
                            disabled={!showOtherReason}
                            tabIndex={showOtherReason ? 0 : -1}
                            rows={2}
                            value={otherReason}
                            onChange={(e) => setOtherReason(e.target.value)}
                            placeholder={TEXT.ENTER_REASON_FOR_SUSPENSION}
                            className="gs-suspend-dialog__textarea"
                        />
                    </div>
                </div>

                <div className="gs-suspend-dialog__footer">
                    <Button
                        type="button"
                        variant="destructive"
                        className={loadingButtonClass(props.loading)}
                        onClick={handleSubmit}
                        disabled={!canSubmit || props.loading}
                        aria-busy={props.loading || undefined}
                    >
                        <ButtonLoadingContent
                            loading={props.loading}
                            spinnerClassName="text-white"
                        >
                            Suspend
                        </ButtonLoadingContent>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SuspendUserModal;
